/**
 * Masters Event Planner — window + scene constants.
 *
 * The demo models Masters 2027 (standard window): activation Apr 2–13, 2027,
 * rounds Thu Apr 8 – Sun Apr 11, exodus Sun Apr 11. Future-dated (~10 months
 * out) so it reads as a live upcoming surge. All times are epoch milliseconds.
 *
 * Kept timezone-stable via Date.UTC so the synthetic generator and timeline are
 * deterministic regardless of the operator's locale.
 */

/** KAGS (Augusta Regional / Bush Field) — the demo location slug. */
export const EVENT_LOCATION_SLUG = 'kags'

/** Masters window — inclusive activation start. */
export const WINDOW_START = Date.UTC(2027, 3, 2, 0, 0, 0) // Apr 2, 2027 00:00 UTC
/** Masters window — end of activation. */
export const WINDOW_END = Date.UTC(2027, 3, 13, 23, 59, 0) // Apr 13, 2027 23:59 UTC

/** The Sunday exodus — dominant departure day. */
export const EXODUS_DAY = Date.UTC(2027, 3, 11, 0, 0, 0) // Sun Apr 11, 2027

/**
 * Default clock position: Saturday Apr 10 noon — near-peak occupancy (after the
 * arrival ramp, before the Sunday exodus) so a freshly-placed apron reads full
 * before the operator touches the timeline.
 */
export const DEFAULT_VIEW_TIME = Date.UTC(2027, 3, 10, 12, 0, 0)

/** Timeline granularity: one slot = 15 minutes. */
export const SLOT_MS = 15 * 60 * 1000

/**
 * Playback rate: at 1x, one real second advances the clock by one 15-min slot.
 * So sim-ms per real-ms at 1x = SLOT_MS / 1000 = 900. Multiply by `speed` (1–8).
 */
export const SIM_MS_PER_REAL_MS_AT_1X = SLOT_MS / 1000 // 900

/** Low floor so the slowest rate (1 sim-sec per real-sec ≈ 0.0011×) is reachable. */
export const MIN_SPEED = 0.001
/** Up to 64× (= 16h/s) so the full ~11-day window can be played through fast. */
export const MAX_SPEED = 64

/**
 * Playback rate presets expressed as **sim time advanced per real second** —
 * far more intuitive than bare multipliers. The internal `speed` multiplier is
 * `simSeconds / (SIM_MS_PER_REAL_MS_AT_1X)` since 1× already advances 900 sim-sec
 * (15 min) per real second.
 */
export interface PlaybackRate {
  label: string
  /** sim seconds advanced per real second of playback */
  simSeconds: number
}
export const PLAYBACK_RATES: PlaybackRate[] = [
  { label: '1s', simSeconds: 1 },
  { label: '5s', simSeconds: 5 },
  { label: '15s', simSeconds: 15 },
  { label: '30s', simSeconds: 30 },
  { label: '1m', simSeconds: 60 },
  { label: '2m', simSeconds: 2 * 60 },
  { label: '5m', simSeconds: 5 * 60 },
  { label: '15m', simSeconds: 15 * 60 },
  { label: '30m', simSeconds: 30 * 60 },
  { label: '1h', simSeconds: 60 * 60 },
  { label: '2h', simSeconds: 2 * 60 * 60 },
  { label: '4h', simSeconds: 4 * 60 * 60 },
  { label: '8h', simSeconds: 8 * 60 * 60 },
]

/** Convert a rate preset (sim-sec per real-sec) to the internal speed multiplier. */
export const rateToSpeed = (simSeconds: number) => simSeconds / SIM_MS_PER_REAL_MS_AT_1X

/**
 * localStorage key for the persisted planner state. Bump on any change that
 * invalidates persisted placements (engine, synthetic count, or KAGS area
 * geometry/size caps) so a stale cache can't mask the new derivation.
 * v2 (2026-06-17): KAGS size-class banding + ITT-J heavy-pad deepening.
 * v3 (2026-06-18): editable fleet/scenario state (count, mix, curves, seed).
 */
export const PERSIST_KEY = 'masters-event-planner-v3'
